export const initialState = {
  isDarkMode: false,
  mainLayout: 'app',
  theme: 'light',
  menu: 'collapsible-vertical',
  layout: 'full',
  rtlClass: 'ltr',
  animation: '',
  navbar: 'navbar-sticky',
  locale: 'en',
  sidebar: false,
  languageList: [
    { code: 'zh', name: 'Chinese' },
    { code: 'da', name: 'Danish' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'el', name: 'Greek' },
    { code: 'hu', name: 'Hungarian' },
    { code: 'it', name: 'Italian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'pl', name: 'Polish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
    { code: 'sv', name: 'Swedish' },
    { code: 'tr', name: 'Turkish' },
    { code: 'ae', name: 'Arabic' },
  ],
  semidark: false,
};

type IndexAction = { type: string; payload?: unknown };

function asPayloadString(payload: unknown, fallback: string): string {
  if (typeof payload === 'string') {
    return payload;
  }
  if (typeof payload === 'number' || typeof payload === 'boolean') {
    return String(payload);
  }
  return fallback;
}

function resolveThemeDarkMode(theme: string, current: boolean): boolean {
  if (theme === 'light') {
    return false;
  }
  if (theme === 'dark') {
    return true;
  }
  if (theme === 'system') {
    return !!globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches;
  }
  return current;
}

function applyBodyDarkClass(isDarkMode: boolean): void {
  const body = document.querySelector('body');
  if (isDarkMode) {
    body?.classList.add('dark');
  } else {
    body?.classList.remove('dark');
  }
}

function reduceToggleTheme(
  state: typeof initialState,
  payload: unknown,
): typeof initialState {
  const theme = asPayloadString(payload, state.theme);
  localStorage.setItem('theme', theme);
  const isDarkMode = resolveThemeDarkMode(theme, state.isDarkMode);
  applyBodyDarkClass(isDarkMode);
  return { ...state, theme, isDarkMode };
}

function reduceToggleAnimation(
  state: typeof initialState,
  payload: unknown,
): typeof initialState {
  const animation = asPayloadString(payload, '').trim();
  localStorage.setItem('animation', animation);
  if (animation) {
    const el = document.querySelector('.animation');
    el?.classList.add('animate__animated');
    el?.classList.add(animation);
  }
  return { ...state, animation };
}

function reduceToggleRTL(
  state: typeof initialState,
  payload: unknown,
): typeof initialState {
  const rtlClass = asPayloadString(payload, state.rtlClass);
  localStorage.setItem('rtlClass', rtlClass);
  document.querySelector('html')?.setAttribute('dir', rtlClass || 'ltr');
  return { ...state, rtlClass };
}

export function indexReducer(
  state = initialState,
  action: IndexAction,
): typeof initialState {
  const { type } = action;
  const payload = action.payload;

  switch (type) {
    case 'toggleDirection': {
      const direction = asPayloadString(payload, 'ltr');
      localStorage.setItem('direction', direction);
      document.querySelector('html')?.setAttribute('dir', direction || 'ltr');
      return { ...state, direction } as typeof initialState;
    }
    case 'setMainLayout':
      return { ...state, mainLayout: payload as string };
    case 'toggleTheme':
      return reduceToggleTheme(state, payload);
    case 'toggleMenu': {
      const menu = asPayloadString(payload, state.menu);
      localStorage.setItem('menu', menu);
      return { ...state, sidebar: false, menu };
    }
    case 'toggleLayout': {
      const layout = asPayloadString(payload, state.layout);
      localStorage.setItem('layout', layout);
      return { ...state, layout };
    }
    case 'toggleRTL':
      return reduceToggleRTL(state, payload);
    case 'toggleAnimation':
      return reduceToggleAnimation(state, payload);
    case 'toggleNavbar': {
      const navbar = asPayloadString(payload, state.navbar);
      localStorage.setItem('navbar', navbar);
      return { ...state, navbar };
    }
    case 'toggleSemidark': {
      const semidark = (payload ?? false) as boolean;
      localStorage.setItem('semidark', String(semidark));
      return { ...state, semidark };
    }
    case 'toggleLocale': {
      const locale = asPayloadString(payload, state.locale);
      localStorage.setItem('i18n_locale', locale);
      return { ...state, locale };
    }
    case 'toggleSidebar':
      return { ...state, sidebar: !state.sidebar };
    default:
      return state;
  }
}
